import {useEffect, useRef} from 'react';
import {usePublicHttpClient} from '~/library/network-facade';
import TenantId from '~/shared/legacy_models/TenantId';
import {fetchSmsStatus, SmsErrorStatus} from '../api/smsStatus';

type StartPollingOptions = {
    onSuccess?: () => void;
    onError?: (errorType: SmsErrorStatus) => void;
    tenantId?: string;
};

const ATTEMPTS_LIMIT = 5;

export const useSmsStatusPolling = () => {
    const http = usePublicHttpClient();

    const cleanupRef = useRef<() => void>();
    const attemptsRef = useRef(ATTEMPTS_LIMIT);

    function stopPolling() {
        cleanupRef.current?.();
    }

    useEffect(() => {
        return stopPolling;
    }, []);

    async function poll(uuid: string, successCb?: () => void, errorCb?: (errorType: SmsErrorStatus) => void) {
        try {
            const data = await fetchSmsStatus(http, uuid);

            if (data.code === 100) {
                stopPolling();
                errorCb?.(data.error_name);
            }

            if (data.code === 0) {
                stopPolling();
                successCb?.();
            }
        } catch (e) {
            stopPolling();
            throw e;
        }
    }

    function startPolling(options?: StartPollingOptions) {
        stopPolling();
        attemptsRef.current = ATTEMPTS_LIMIT;
        const tenantId = new TenantId().get() ?? options?.tenantId;

        if (!tenantId) {
            throw new Error('Tenant id is not defined');
        }

        const execute = () => poll(tenantId, options?.onSuccess, options?.onError);

        let cleanupInterval: () => void = noop;

        const timeoutId = setTimeout(() => {
            execute();

            cleanupInterval = setIrregularInterval(async () => {
                if (attemptsRef.current <= 0) {
                    stopPolling();
                    return;
                }

                execute();

                attemptsRef.current = attemptsRef.current - 1;
            }, 3000);
        }, 1000);

        cleanupRef.current = () => {
            clearTimeout(timeoutId);
            cleanupInterval();
        };
    }

    return {startPolling: startPolling} as const;
};

function setIrregularInterval(callback: () => void, ms: number) {
    let timeoutId: number;
    const intervalId = window.setInterval(() => {
        const time = Math.random() * (ms * 0.25);
        timeoutId = window.setTimeout(() => {
            callback();
        }, time);
    }, ms);

    return () => {
        window.clearTimeout(timeoutId);
        window.clearInterval(intervalId);
    };
}

function noop() {}
